// localStorage persistence for the in-memory TMDB cache (`tmdb.js`'s tmdbCache).
//
// WHY: the in-memory Map's 30-min / 24h TTLs only ever lived inside ONE tab and
// were wiped on every reload — so a cold home-page load re-fetched every shelf,
// genre list, and per-card provider lookup from scratch, hammering the API. By
// snapshotting the Map to localStorage we make those TTLs survive reloads and
// back/forward navigation: a revisit within the TTL serves entirely from disk
// and makes ZERO network calls. (The server proxy still dedupes across users;
// this layer dedupes across reloads for one device.)
//
// The pure helpers (prune/cap/parse/serialize) take values + `now` injectably so
// they're unit-testable under `node --test`; the localStorage glue self-guards
// against quota / private-mode / corrupt-JSON failures and never throws.

export const STORAGE_KEY = "putio_tmdb_cache_v1";

// Cap the number of persisted entries so a long browsing session can't grow the
// snapshot unbounded. We keep the entries that expire LATEST — those are the
// stable, expensive-to-recompute ones (24h provider + genre lookups) most worth
// surviving a reload. ~700 comfortably covers a full home + discover fan-out.
export const MAX_PERSISTED_ENTRIES = 700;

// Skip writing if the serialized snapshot would exceed this (localStorage is
// ~5 MB; stay well under so we never evict the app's other keys).
export const MAX_SNAPSHOT_BYTES = 2_500_000;

/** Drop entries whose expiresAt has passed. `entries` is [key, {value, expiresAt}][]. */
export function pruneEntries(entries, now = Date.now()) {
  if (!Array.isArray(entries)) return [];
  return entries.filter(
    ([, v]) => v && typeof v.expiresAt === "number" && v.expiresAt > now,
  );
}

/** Keep at most `max` entries, preferring those that expire latest. */
export function capEntries(entries, max = MAX_PERSISTED_ENTRIES) {
  if (!Array.isArray(entries) || entries.length <= max) return entries || [];
  return [...entries]
    .sort((a, b) => (b[1]?.expiresAt || 0) - (a[1]?.expiresAt || 0))
    .slice(0, max);
}

/** Serialize a [key, entry][] list to the stored envelope string, or "" if too big. */
export function serializeSnapshot(entries) {
  try {
    const json = JSON.stringify({ v: 1, entries });
    if (json.length > MAX_SNAPSHOT_BYTES) return "";
    return json;
  } catch {
    return "";
  }
}

/** Parse a stored envelope back to a [key, entry][] list; [] on any problem. */
export function parseSnapshot(raw) {
  if (typeof raw !== "string" || raw === "") return [];
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.entries)) return [];
    return parsed.entries.filter(
      (e) =>
        Array.isArray(e) &&
        typeof e[0] === "string" &&
        e[1] &&
        typeof e[1].expiresAt === "number",
    );
  } catch {
    return [];
  }
}

function lsOrNull() {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Hydrate `map` (the live tmdbCache) from the persisted snapshot, dropping
 * anything already expired. Existing in-memory entries win (they're fresher).
 * Returns the number of entries restored.
 */
export function hydrateFromStorage(map, now = Date.now()) {
  const ls = lsOrNull();
  if (!ls || !map) return 0;
  let restored = 0;
  try {
    const entries = pruneEntries(parseSnapshot(ls.getItem(STORAGE_KEY)), now);
    for (const [key, entry] of entries) {
      if (!map.has(key)) {
        map.set(key, entry);
        restored += 1;
      }
    }
  } catch {
    /* ignore */
  }
  return restored;
}

/** Write the current Map to localStorage (pruned + capped). Never throws. */
export function persistToStorage(map, now = Date.now()) {
  const ls = lsOrNull();
  if (!ls || !map) return false;
  try {
    const live = capEntries(pruneEntries([...map.entries()], now));
    const raw = serializeSnapshot(live);
    if (!raw) {
      // Too big to store — clear any stale snapshot rather than leave a lie.
      try {
        ls.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      return false;
    }
    ls.setItem(STORAGE_KEY, raw);
    return true;
  } catch {
    return false;
  }
}

// --- debounced + flush-on-hide saver -----------------------------------------
//
// Returns a `schedule()` to call after cache writes (debounced) and wires a
// pagehide/visibilitychange flush so an in-progress dwell isn't lost on tab
// close. Idempotent listener registration guarded by `wired`.
//
// The persist work (entries→prune→sort→stringify up to 2.5 MB→sync setItem)
// is expensive, so the debounce is long (10s — a shelf-load burst pays it at
// most once) and the timer fires into requestIdleCallback so the work runs
// off the critical path. The pagehide/hidden `flush` stays SYNCHRONOUS —
// idle callbacks may never run in a closing/backgrounded tab, and that's the
// data-loss window the flush exists to cover.
export function createPersistScheduler(map, { delayMs = 10_000 } = {}) {
  let timer = null;
  let wired = false;

  const persistWhenIdle = () => {
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => persistToStorage(map), { timeout: 5000 });
    } else {
      setTimeout(() => persistToStorage(map), 0);
    }
  };

  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    persistToStorage(map);
  };

  const schedule = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      persistWhenIdle();
    }, delayMs);
  };

  if (typeof window !== "undefined" && !wired) {
    wired = true;
    const onHide = () => {
      if (
        typeof document === "undefined" ||
        document.visibilityState === "hidden"
      ) {
        flush();
      }
    };
    try {
      window.addEventListener("pagehide", flush);
      document.addEventListener("visibilitychange", onHide);
    } catch {
      /* SSR / no DOM */
    }
  }

  return { schedule, flush };
}
